import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import socket from "../Sales1/socket";
import { WA_HISTORY } from "../../../Queries/WhatsApp";
import { openTab } from "../../../Slices/tabSlice";
import { fetchWaStatus, fetchWaQuickReplies, setWaActive } from "../../../Slices/whatsappSlice";
import ConversationPane from "./ConversationPane";
import NewConversationPanel from "./NewConversationPanel";
import ConsentForm from "./ConsentForm";
import { gqlErrorMessage, listTime } from "./waUtils";
import "./whatsapp.css";

const REFRESH_DEBOUNCE_MS = 800;
const CONSENT_CHIP = {
  opted_in: { cls: "wa-chip--ok", label: "Opted in" },
  opted_out: { cls: "wa-chip--danger", label: "Opted out" },
  unknown: { cls: "", label: "No consent" },
};
// Meta has no "is this number on WhatsApp?" lookup — this is learned from real traffic.
const REACH_CHIP = {
  yes: { cls: "wa-chip--ok", icon: "fa-solid fa-circle-check", label: "On WhatsApp", title: "This number has messaged us or received our messages on WhatsApp." },
  no: { cls: "wa-chip--danger", icon: "fa-solid fa-circle-xmark", label: "Likely not on WhatsApp", title: "Meta couldn't deliver to this number (not on WhatsApp, an old app, or terms not accepted)." },
  unknown: { cls: "", icon: "fa-regular fa-circle-question", label: "Not checked yet", title: "WhatsApp doesn't let businesses look numbers up — the first message sent will confirm it." },
};

const WA_ROUTES = ["/whatsapp", "/whatsapp-admin"];
const ADMIN_ROLES = ["Admin", "Super Admin"];

/** Should the WhatsApp tab be shown? (Server still enforces access.) */
export const useCanSeeWhatsApp = () => {
  const routes = useSelector((s) => s?.employee?.currentEmployeeAccess?.routes) || [];
  const role = useSelector((s) => s?.loginUserInfo?.loginUserDetails?.role);
  return ADMIN_ROLES.includes(role) || routes.some((r) => WA_ROUTES.includes(r));
};

const byRecent = (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);

/**
 * WhatsApp tab on a Lead or Client page: the record's contacts (with
 * WhatsApp reachability + consent) and a full live chat for each thread —
 * reply, templates, attachments and notes without leaving the record. It
 * shares state with the inbox, so both stay in sync in real time.
 */
const WhatsAppRecordTab = ({ leadId, clientId, recordName, active = true }) => {
  const dispatch = useDispatch();
  const { status, statusLoaded } = useSelector((s) => s.whatsapp);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consentFor, setConsentFor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [panel, setPanel] = useState(null); // { type: "new", initial, key }
  const timer = useRef(null);
  const convIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!leadId && !clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.query({ query: WA_HISTORY, variables: { leadId: leadId || null, clientId: clientId || null }, fetchPolicy: "network-only" });
      const h = res.data.whatsappHistory;
      convIdsRef.current = new Set((h.conversations || []).map((c) => c.id));
      setData(h);
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [leadId, clientId]);

  // Load when the tab becomes visible (the lead page keeps hidden tabs mounted).
  useEffect(() => {
    if (!active) return;
    load();
    if (!statusLoaded) dispatch(fetchWaStatus());
  }, [active, load, statusLoaded, dispatch]);

  useEffect(() => {
    if (active && status?.canUseInbox) dispatch(fetchWaQuickReplies());
  }, [active, status?.canUseInbox, dispatch]);

  const conversations = useMemo(() => [...(data?.conversations || [])].sort(byRecent), [data]);

  // Keep a valid selection: the most recent thread by default.
  useEffect(() => {
    if (!conversations.length) return;
    if (!selectedId || !conversations.some((c) => c.id === selectedId)) setSelectedId(conversations[0].id);
  }, [conversations, selectedId]);

  // Refresh contacts/threads (reachability, new threads) when this record's conversations change.
  useEffect(() => {
    const refresh = (payload) => {
      const conv = payload?.conversation || payload;
      const convId = conv?.id || payload?.conversationId;
      const mine = (convId && convIdsRef.current.has(convId)) || (leadId && conv?.leadId === leadId) || (clientId && conv?.clientId === clientId);
      if (!mine || !active) return;
      clearTimeout(timer.current);
      timer.current = setTimeout(load, REFRESH_DEBOUNCE_MS);
    };
    socket.on("wa:message:new", refresh);
    socket.on("wa:message:status", refresh);
    socket.on("wa:conversation:updated", refresh);
    return () => {
      clearTimeout(timer.current);
      socket.off("wa:message:new", refresh);
      socket.off("wa:message:status", refresh);
      socket.off("wa:conversation:updated", refresh);
    };
  }, [leadId, clientId, active, load]);

  const openInInbox = (conversationId) => {
    dispatch(setWaActive(conversationId));
    dispatch(openTab({ path: "/whatsapp", title: "WhatsApp", deduplicate: true }));
  };

  const startMessage = (c) => {
    setPanel({
      type: "new",
      key: `${c.kind}-${c.contactId}-${Date.now()}`,
      initial: {
        phone: c.phone,
        contactName: c.name,
        recordName: c.recordName || recordName || "",
        leadId: c.kind === "lead" ? c.recordId : null,
        leadContactId: c.kind === "lead" ? c.contactId : null,
        clientId: c.kind === "client" ? c.recordId : null,
      },
    });
  };

  if (error) {
    const noAccess = /access/i.test(error);
    return (
      <div className="wa-record-tab">
        <div className={`wa-alert ${noAccess ? "wa-alert--info" : "wa-alert--danger"}`}>
          <i className={`fa-solid ${noAccess ? "fa-lock" : "fa-circle-exclamation"}`} />
          <div style={{ flex: 1 }}>{noAccess ? "WhatsApp is available to users with WhatsApp Inbox access." : error}</div>
          {!noAccess && <button type="button" className="wa-btn wa-btn--sm" onClick={load}>Retry</button>}
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="wa-record-tab"><div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading WhatsApp…</div></div>;
  }

  const contacts = data.contacts || [];
  const enabled = status?.enabled !== false;

  return (
    <div className="wa-record-tab">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <i className="fa-brands fa-whatsapp" style={{ fontSize: 18, color: "var(--wa-accent)" }} />
        <b style={{ fontSize: 15, flex: 1 }}>WhatsApp</b>
        <button type="button" className="wa-icon-btn" onClick={load} disabled={loading} aria-label="Refresh" title="Refresh">
          <i className={`fa-solid fa-rotate ${loading ? "wa-spin" : ""}`} />
        </button>
      </div>

      {!enabled && (
        <div className="wa-alert wa-alert--warn" style={{ marginBottom: 12 }}>
          <i className="fa-solid fa-plug-circle-xmark" /><div>WhatsApp isn't connected on the server yet — messages can't be sent.</div>
        </div>
      )}

      <div className="wa-section__title" style={{ marginBottom: 6 }}>Contacts</div>
      {!contacts.length && <div className="wa-help" style={{ marginBottom: 12 }}>No contacts on this record.</div>}
      {contacts.map((c) => {
        const consent = c.waId ? CONSENT_CHIP[c.optInStatus || "unknown"] : null;
        const reach = c.waId ? REACH_CHIP[c.reachability?.status || "unknown"] : null;
        const key = `${c.kind}-${c.contactId}`;
        const selected = !!c.conversationId && c.conversationId === selectedId;
        return (
          <div className={`wa-record ${selected ? "wa-record--selected" : ""}`} key={key} style={{ padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                <b>{c.name || "Unnamed contact"}</b>{c.title ? <span className="wa-help"> · {c.title}</span> : null}
                <div className="wa-help" style={{ margin: 0 }}>{c.waId ? c.displayNumber : c.phone ? `${c.phone} — not a valid phone number` : "No phone on file"}</div>
              </div>
              {reach && <span className={`wa-chip ${reach.cls}`} title={reach.title}><i className={reach.icon} /> {reach.label}</span>}
              {consent && <span className={`wa-chip ${consent.cls}`}>{consent.label}</span>}
              {c.conversationId && !selected && (
                <button type="button" className="wa-btn wa-btn--sm" onClick={() => setSelectedId(c.conversationId)}>
                  <i className="fa-regular fa-comments" /> Open chat
                </button>
              )}
              {c.waId && !c.conversationId && c.optInStatus !== "opted_out" && enabled && (
                <button type="button" className="wa-btn wa-btn--sm wa-btn--primary" onClick={() => startMessage(c)}>
                  <i className="fa-brands fa-whatsapp" /> Message
                </button>
              )}
              {c.waId && c.optInStatus !== "opted_in" && c.optInStatus !== "opted_out" && (
                <button type="button" className="wa-btn wa-btn--sm" onClick={() => setConsentFor(consentFor === key ? null : key)}>
                  <i className="fa-solid fa-user-check" /> Record opt-in
                </button>
              )}
            </div>
            {consentFor === key && (
              <div style={{ marginTop: 10 }}>
                <ConsentForm compact phone={c.phone} leadId={c.kind === "lead" ? c.recordId : null} onSaved={() => { setConsentFor(null); load(); }} />
              </div>
            )}
          </div>
        );
      })}

      <div className="wa-section__title" style={{ margin: "16px 0 6px" }}>
        Conversation
        {conversations.length > 1 && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{conversations.length} threads</span>}
      </div>
      {conversations.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }} role="tablist" aria-label="Threads">
          {conversations.map((c) => (
            <button
              type="button"
              role="tab"
              aria-selected={c.id === selectedId}
              key={c.id}
              className={`wa-chip ${c.id === selectedId ? "wa-chip--accent" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedId(c.id)}
            >
              <span className={`wa-dot ${c.windowOpen ? "wa-dot--open" : ""}`} /> {c.contactName || c.displayNumber} · {listTime(c.lastMessageAt)}
              {c.unreadCount > 0 && <span className="wa-unread" style={{ marginLeft: 4 }}>{c.unreadCount}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Chat box is its own .wa-root so the pane's side panels stay inside it. Only mounted while visible (mark-as-read). */}
      {active && (
        <div className="wa-root has-active wa-record-chat">
          {selectedId ? (
            <ConversationPane
              key={selectedId}
              conversationId={selectedId}
              headerActions={(
                <button type="button" className="wa-icon-btn" onClick={() => openInInbox(selectedId)} aria-label="Open in Inbox" title="Open in Inbox">
                  <i className="fa-solid fa-up-right-from-square" />
                </button>
              )}
            />
          ) : (
            <div className="wa-thread" role="region" aria-label="Conversation">
              <div className="wa-empty">
                <i className="fa-brands fa-whatsapp wa-empty__icon" style={{ fontSize: 40 }} />
                <div className="wa-empty__title">No WhatsApp messages with this {leadId ? "lead" : "client"} yet</div>
                <div>Click <b>Message</b> next to a contact to start with an approved template.</div>
              </div>
            </div>
          )}
          {panel?.type === "new" && (
            <NewConversationPanel
              key={panel.key}
              initial={panel.initial}
              onClose={() => setPanel(null)}
              onStarted={(conv) => {
                if (conv?.id) setSelectedId(conv.id);
                load();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppRecordTab;
